/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from 'react'

import { Alert, Fade, Snackbar, styled } from '@mui/material'
import Grid from '@mui/material/Grid'
import axios from 'axios'
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage'

import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'
import { storage } from 'config/firebase'
import { SUB_DOMAIN_MAPPINGS } from 'config/subDomain'
import Footer from 'modules/Footer'
import PageLayout from 'modules/LayoutContainers/PageLayout'
import LoadingComponent from 'modules/Loading'

import MainForm from './components/MainForm'
import { FormContext, INIT_FEEDBACK } from './constants'
import { CustomerFeedback } from './types'
import { AnyFile, RemoteFile } from '../types'
import { getFilenameFromFirebaseUrl } from '../utils'

const MainContent = styled(MDBox)(({ theme }) => ({
	marginTop: theme.spacing(5),
	marginRight: theme.spacing(5),
	marginLeft: theme.spacing(5),
}))

const HeadingBox = styled(MDBox)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	marginBottom: theme.spacing(3),
}))

function CustomerFeedbackPage(): JSX.Element {
	const [localForm, setLocalForm] = useState<CustomerFeedback>(INIT_FEEDBACK)
	const [formEdited, setFormEdited] = useState<boolean>(false)
	const [showError, setShowError] = useState<boolean>(false)
	const [loading, setLoading] = useState<boolean>(false)
	const [snackbarMessages, setSnackbarMessages] = useState<{ msg: string; open: boolean }[]>([])

	const pushSnackbarMessage = (message: string) => {
		setSnackbarMessages(prev => [...prev, { msg: message, open: true }])
	}

	const uploadFile = async (file: AnyFile, path: string): Promise<string> => {
		if (file.type === 'local') {
			const storageRef = ref(storage, `${path}/${file.file.name}`)
			await uploadBytes(storageRef, file.file)
			return await getDownloadURL(storageRef)
		}
		return file.url
	}

	const handleAttachmentUploads = async (files: AnyFile[], newFeedbackId?: string) => {
		if (!files || files.length === 0) return []

		const vendorDomain = window.location.hostname.split('.')[0]
		const orgId = Object.keys(SUB_DOMAIN_MAPPINGS).includes(vendorDomain)
			? SUB_DOMAIN_MAPPINGS[vendorDomain as keyof typeof SUB_DOMAIN_MAPPINGS]
			: 'default'

		const path = `laurel/${orgId}/feedback/${newFeedbackId ?? localForm.form.details.id}`

		const results = await Promise.allSettled(files.map(file => uploadFile(file, path)))
		const fulfilledRemoteAttachments = results
			.map((result, index) => {
				if (result.status === 'fulfilled') {
					const remoteFile: RemoteFile = {
						type: 'remote',
						url: result.value,
					}
					return remoteFile
				} else {
					const rejectedFile = files[index]
					const rejectedFileName =
						rejectedFile.type === 'local'
							? rejectedFile.file.name
							: getFilenameFromFirebaseUrl(rejectedFile.url)
					setLocalForm(prev => ({
						...prev,
						form: {
							...prev.form,
							details: {
								...prev.form.details,
								attachments: prev.form.details.attachments.filter(
									attachment => attachment !== rejectedFile
								),
							},
						},
					}))
					pushSnackbarMessage(`Failed to upload file ${rejectedFileName}`)
					return null
				}
			})
			.filter(url => url)

		return fulfilledRemoteAttachments
	}

	const saveNewFeedback = async () => {
		const localFormWithoutAttachments: CustomerFeedback = {
			...localForm,
			form: {
				...localForm.form,
				details: {
					...localForm.form.details,
					attachments: [],
				},
			},
		}

		const response = await axios.post<{ id: string }>(
			`${process.env.REACT_APP_API_URL}/feedback/customer`,
			localFormWithoutAttachments
		)
		return response.data
	}

	const addAttachmentsToNewlyCreatedFeedback = async (
		newAttachmentUrls: RemoteFile[],
		newFeedbackId: string
	) => {
		const localFormWithAttachments: CustomerFeedback = {
			...localForm,
			form: {
				...localForm.form,
				details: {
					...localForm.form.details,
					id: newFeedbackId,
					attachments: newAttachmentUrls,
				},
			},
		}

		const response = await axios.patch<CustomerFeedback>(
			`${process.env.REACT_APP_API_URL}/feedback/customer`,
			localFormWithAttachments
		)
		return response.data
	}

	useEffect(() => {
		if (snackbarMessages.length > 0 && snackbarMessages.every(snackbar => !snackbar.open)) {
			const timeout = setTimeout(() => {
				setSnackbarMessages([])
			}, 500)
			return () => clearTimeout(timeout)
		}
	}, [snackbarMessages])

	return (
		<FormContext.Provider
			value={{ localForm, setLocalForm, formEdited, setFormEdited, showError, setShowError }}
		>
			<PageLayout>
				{snackbarMessages.map((snackbar, index) => (
					<Snackbar
						key={index}
						open={snackbar.open}
						autoHideDuration={8000}
						onClose={() =>
							setSnackbarMessages(prev =>
								prev.map((item, i) => (i === index ? { ...item, open: false } : item))
							)
						}
						anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
						TransitionComponent={Fade}
						style={{
							bottom: `${20 + index * 60}px`,
							right: '20px',
							position: 'fixed',
						}}
					>
						<Alert
							severity={snackbar.msg.toLowerCase().includes('error') ? 'error' : 'info'}
							variant="standard"
							sx={{ width: '100%' }}
						>
							{snackbar.msg}
						</Alert>
					</Snackbar>
				))}

				<MainContent>
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<HeadingBox>
								<MDTypography variant="h2">New Feedback</MDTypography>
							</HeadingBox>
							<MainForm />
						</Grid>
						<Grid item xs={12}>
							<MDBox display="flex" justifyContent="center">
								<MDButton
									color="primary"
									variant="outlined"
									disabled={!formEdited}
									onClick={async () => {
										setLoading(true)
										try {
											setFormEdited(false)
											const newFeedback = await saveNewFeedback()
											const downloadUrls = await handleAttachmentUploads(
												localForm.form.details.attachments,
												newFeedback.id
											)
											if (localForm.form.details.attachments.length > 0) {
												await addAttachmentsToNewlyCreatedFeedback(downloadUrls, newFeedback.id)
											}
											setShowError(false)
											pushSnackbarMessage(
												`Feedback submitted successfully. Refence ID: #REF0000${newFeedback.id}`
											)
											setLocalForm(INIT_FEEDBACK)
										} catch {
											pushSnackbarMessage('Error: Could not save feedback. Please try again later.')
											setShowError(true)
										}
										setLoading(false)
									}}
									sx={{ width: '20em' }}
								>
									SUBMIT
									{loading && (
										<LoadingComponent type="circular_black" height={22} width={22} speed={0.9} />
									)}
								</MDButton>
							</MDBox>
						</Grid>
					</Grid>
				</MainContent>
				<Footer />
			</PageLayout>
		</FormContext.Provider>
	)
}

export default CustomerFeedbackPage
